import Balances from '@/components/modules/Balances';
import MarketList from '@/components/modules/MarketList';
import OrderBook from '@/components/modules/OrderBook';
import OrderExecution from '@/components/modules/OrderExecution';
import { useAppSelector } from '@/state/hooks';
import {
    selectXplorSpotDesktopLayoutNode,
    selectXplorSpotLaptopLayoutNode,
    selectXplorSpotMobileLayoutNode,
    selectXplorSpotTabletLayoutNode
} from '@/state/reducers/xplorSpotLayoutSlice';
import { useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import {
    MdOutlineAccountBalanceWallet,
    MdOutlineCandlestickChart,
    MdOutlineHistory,
    MdOutlinePivotTableChart,
    MdOutlinePriceChange,
    MdOutlineSwapVerticalCircle
} from 'react-icons/md';
import { MosaicNode } from 'react-mosaic-component';
import useLanguage from './useLanguage';
import { UseScreenSizesReturn } from './useScreenSizes';

export type XplorSpotModules = 'Order' | 'Balances' | 'Chart' | 'Order Book' | 'Blotter' | 'Markets';
export const tabList = ['Order', 'Balances', 'Chart', 'Order Book', 'Blotter', 'Markets'];
export const tabIcon = {
    Order: MdOutlineSwapVerticalCircle,
    Balances: MdOutlineAccountBalanceWallet,
    Chart: MdOutlineCandlestickChart,
    'Order Book': MdOutlinePriceChange,
    Blotter: MdOutlineHistory,
    Markets: MdOutlinePivotTableChart
} as Record<XplorSpotModules, IconType>;

export const DEFAULT_LAYOUT: MosaicNode<XplorSpotModules> = {
    direction: 'row',
    first: {
        direction: 'row',
        first: {
            direction: 'column',
            first: 'Order',
            second: 'Balances',
            splitPercentage: 60
        },
        second: {
            direction: 'column',
            first: {
                direction: 'row',
                first: 'Order Book',
                second: 'Chart',
                splitPercentage: 40
            },
            second: 'Blotter',
            splitPercentage: 60
        },
        splitPercentage: 30
    },
    second: 'Markets',
    splitPercentage: 75
};

export const DEFAULT_MOBILE_LAYOUT: MosaicNode<XplorSpotModules> = {
    direction: 'row',
    first: 'Order',
    second: 'Chart',
    splitPercentage: 100
};

export const DEFAULT_TABLET_LAYOUT: MosaicNode<XplorSpotModules> = {
    direction: 'row',
    first: 'Chart',
    second: 'Order',
    splitPercentage: 60
};

export const DEFAULT_LAPTOP_LAYOUT: MosaicNode<XplorSpotModules> = {
    direction: 'row',
    first: {
        direction: 'row',
        first: {
            direction: 'column',
            first: 'Order',
            second: 'Balances',
            splitPercentage: 60
        },
        second: {
            direction: 'column',
            first: 'Chart',
            second: 'Blotter',
            splitPercentage: 60
        },
        splitPercentage: 40
    },
    second: {
        direction: 'column',
        first: 'Markets',
        second: 'Order Book',
        splitPercentage: 60
    },
    splitPercentage: 65
};

export type ConfigType = Record<
    string,
    {
        title: string;
        component?: JSX.Element;
    }
>;

export interface UseXplorSpotLayoutReturn {
    layout: MosaicNode<XplorSpotModules> | null | undefined | any;
    default_layout: MosaicNode<XplorSpotModules>;
    config: ConfigType;
}

const useXplorSpotLayout = (screen: UseScreenSizesReturn): UseXplorSpotLayoutReturn => {
    const lang = useLanguage();
    const mobileLayoutNode = useAppSelector(selectXplorSpotMobileLayoutNode);
    const tabletLayoutNode = useAppSelector(selectXplorSpotTabletLayoutNode);
    const laptopLayoutNode = useAppSelector(selectXplorSpotLaptopLayoutNode);
    const desktopLayoutNode = useAppSelector(selectXplorSpotDesktopLayoutNode);

    const config: ConfigType = useMemo(() => {
        return {
            Order: {
                title: lang.modules.order,
                component: (
                    <div id="orderform" className="h-full bg-brand-background">
                        <OrderExecution />
                    </div>
                )
            },
            Balances: {
                title: lang.modules.balances,
                component: (
                    <div id="balances" className="h-full bg-brand-background">
                        <Balances />
                    </div>
                )
            },
            Chart: {
                title: lang.modules.chart
            },
            'Order Book': {
                title: lang.modules['order-book'],
                component: <OrderBook />
            },
            Blotter: {
                title: lang.modules.blotter
            },
            Markets: {
                title: lang.modules.markets,
                component: (
                    <div id="market-list" className="bg-brand-background h-full max-h-full overflow-hidden">
                        <MarketList />
                    </div>
                )
            }
        };
    }, [lang]);

    const xplorSpotLayout = useMemo(() => {
        switch (screen.current) {
            case 'mobile':
                return {
                    layout: mobileLayoutNode || DEFAULT_MOBILE_LAYOUT,
                    default_layout: DEFAULT_MOBILE_LAYOUT
                };
            case 'tablet':
                return {
                    layout: mobileLayoutNode || DEFAULT_MOBILE_LAYOUT,
                    default_layout: DEFAULT_MOBILE_LAYOUT
                };
            case 'laptop':
                return {
                    layout: laptopLayoutNode || DEFAULT_LAPTOP_LAYOUT,
                    default_layout: DEFAULT_LAPTOP_LAYOUT
                };
            case 'desktop':
                return {
                    layout: desktopLayoutNode || DEFAULT_LAYOUT,
                    default_layout: DEFAULT_LAYOUT
                };
            default:
                return {
                    layout: desktopLayoutNode || DEFAULT_LAYOUT,
                    default_layout: DEFAULT_LAYOUT
                };
        }
    }, [screen.current, mobileLayoutNode, tabletLayoutNode, laptopLayoutNode, desktopLayoutNode]);

    return { ...xplorSpotLayout, config };
};

export default useXplorSpotLayout;
